import { getGroupDir } from "./getGroupDir.js";
import { parsePath } from "./parsePath.js";
import {
  getOpfsRootDirName,
  getStorageStatus,
  invalidateStorageRoot,
  isStaleHandleError,
} from "./storage.js";
import { writeFileHandle, writeOpfsPathViaWorker } from "./writeFileHandle.js";
import type { ShadowClawDatabase } from "../db/types.js";

/**
 * Copy a file or directory in a group's workspace to a new path.
 */
export async function copyGroupEntry(
  db: ShadowClawDatabase,
  sourceGroupId: string,
  targetGroupId: string,
  sourcePath: string,
  targetPath: string,
): Promise<void> {
  const normSource = sourcePath.replace(/\/$/, "");
  const normTarget = targetPath.replace(/\/$/, "");

  if (sourceGroupId === targetGroupId && normSource === normTarget) {
    throw new Error("Source and target paths are identical");
  }

  const { dirs: srcDirs, filename: srcName } = parsePath(normSource);
  const { dirs: tgtDirs, filename: tgtName } = parsePath(normTarget);

  if (!srcName || !tgtName) {
    throw new Error("Invalid path");
  }

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const srcGroupDir = await getGroupDir(db, sourceGroupId);
      const tgtGroupDir = await getGroupDir(db, targetGroupId);

      // Resolve source parent
      let srcParent = srcGroupDir;
      for (const seg of srcDirs) {
        srcParent = await srcParent.getDirectoryHandle(seg);
      }

      // Resolve target parent
      let tgtParent = tgtGroupDir;
      for (const seg of tgtDirs) {
        tgtParent = await tgtParent.getDirectoryHandle(seg, { create: true });
      }

      // Assert target does not exist
      if (
        (await tryGetFileHandle(tgtParent, tgtName)) ||
        (await tryGetDirectoryHandle(tgtParent, tgtName))
      ) {
        throw new Error(`Target already exists: ${tgtName}`);
      }

      // Check if source is a directory
      const sourceDir = await tryGetDirectoryHandle(srcParent, srcName);
      if (sourceDir) {
        const targetDir = await tgtParent.getDirectoryHandle(tgtName, {
          create: true,
        });

        const safeId = targetGroupId.replace(/:/g, "-");
        const currentPathSegments = [
          getOpfsRootDirName(),
          "groups",
          safeId,
          ...tgtDirs,
          tgtName,
        ];

        try {
          await copyDirectoryContents(
            db,
            targetGroupId,
            sourceDir,
            targetDir,
            currentPathSegments,
          );
        } catch (copyErr) {
          await tgtParent
            .removeEntry(tgtName, { recursive: true })
            .catch(() => undefined);
          throw copyErr;
        }

        return;
      }

      // Check if source is a file
      const sourceFile = await tryGetFileHandle(srcParent, srcName);
      if (sourceFile) {
        const file = await sourceFile.getFile();
        const targetFile = await tgtParent.getFileHandle(tgtName, {
          create: true,
        });

        try {
          await writeFileHandle(targetFile, file);
        } catch (writeErr) {
          const message =
            writeErr instanceof Error ? writeErr.message : String(writeErr);
          const needsOpfsWorkerFallback =
            message.includes("Writable file streams are not supported") &&
            (await getStorageStatus(db)).type === "opfs";

          if (!needsOpfsWorkerFallback) {
            await tgtParent.removeEntry(tgtName).catch(() => undefined);

            throw writeErr;
          }

          const safeId = targetGroupId.replace(/:/g, "-");
          await writeOpfsPathViaWorker(
            [getOpfsRootDirName(), "groups", safeId, ...tgtDirs, tgtName],
            file,
          );
        }

        return;
      }

      throw new Error(`Entry not found: ${sourcePath}`);
    } catch (err) {
      if (attempt === 0 && isStaleHandleError(err)) {
        invalidateStorageRoot();

        continue;
      }

      throw err;
    }
  }
}

async function tryGetDirectoryHandle(
  parentDir: FileSystemDirectoryHandle,
  name: string,
): Promise<FileSystemDirectoryHandle | null> {
  try {
    return await parentDir.getDirectoryHandle(name);
  } catch {
    return null;
  }
}

async function tryGetFileHandle(
  parentDir: FileSystemDirectoryHandle,
  name: string,
): Promise<FileSystemFileHandle | null> {
  try {
    return await parentDir.getFileHandle(name);
  } catch {
    return null;
  }
}

async function copyDirectoryContents(
  db: ShadowClawDatabase,
  targetGroupId: string,
  sourceDir: FileSystemDirectoryHandle,
  targetDir: FileSystemDirectoryHandle,
  currentPathSegments: string[],
): Promise<void> {
  for await (const [name, handle] of (sourceDir as any).entries()) {
    if (handle.kind === "directory") {
      const nextTargetDir = await targetDir.getDirectoryHandle(name, {
        create: true,
      });
      await copyDirectoryContents(db, targetGroupId, handle, nextTargetDir, [
        ...currentPathSegments,
        name,
      ]);

      continue;
    }

    const file = await handle.getFile();
    const targetFileHandle = await targetDir.getFileHandle(name, {
      create: true,
    });

    try {
      await writeFileHandle(targetFileHandle, file);
    } catch (writeErr) {
      const message =
        writeErr instanceof Error ? writeErr.message : String(writeErr);
      const needsOpfsWorkerFallback =
        message.includes("Writable file streams are not supported") &&
        (await getStorageStatus(db)).type === "opfs";

      if (!needsOpfsWorkerFallback) {
        throw writeErr;
      }

      await writeOpfsPathViaWorker([...currentPathSegments, name], file);
    }
  }
}
